import { Box, LinearProgress, Link, Typography } from "@mui/material"
import type { FC } from "react"
import { useShallow } from "zustand/react/shallow"

import { type DetailField, DetailFieldList } from "./DetailFieldList"

import { PermissionActionType } from "@/constants/permissions"
import type { GoodsReceiptPallet, GoodsReceiptPalletRowLot } from "@/graphql/codegen/graphql"
import { GoodsReceiptStatus } from "@/graphql/codegen/graphql"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { isAkrochemManagedWarehouse } from "@/screens/WarehouseReceiving/utils"
import { useUnitOfMeasurementsStore } from "@/stores/unitOfMeasurmentsStore"
import { calculateRemainingShelfLife } from "@/utils/product"
import { getUomName } from "@/utils/uom"
import { isNil } from "@/utils/utils"
import { formatWarehouseLabel } from "@/utils/warehouse"

interface ScannedGoodsReceiptPalletCardDetailsProps {
  pallet: GoodsReceiptPallet
  onToggleExpanded: () => void
  onDestinationOverride?: () => void
  onQrPrint: () => void
}

function createLotDetails(lot: GoodsReceiptPalletRowLot, uomName: string): DetailField[] {
  const { expirationDate, manufacturingDate } = lot.productTest || {}

  const remainingShelfLife = calculateRemainingShelfLife(manufacturingDate, expirationDate)

  const detailFields: DetailField[] = [
    ["Lot #:", `${lot.lotNumber} ${lot.totalUom ? `(${lot.totalUom} ${uomName})` : ""}`],
    ["Manufacturing Date:", manufacturingDate],
    ["Expiration Date:", expirationDate],
    [
      "Remaining Shelf Life:",
      <Box className='flex items-center gap-2'>
        {isNil(remainingShelfLife) ? (
          "-"
        ) : (
          <>
            <LinearProgress variant='determinate' value={remainingShelfLife} className='h-2 flex-1 rounded' />
            <Typography variant='caption' fontWeight={500}>
              {remainingShelfLife} %
            </Typography>
          </>
        )}
      </Box>,
    ],
  ]

  return detailFields
}

export const ScannedGoodsReceiptPalletCardDetails: FC<ScannedGoodsReceiptPalletCardDetailsProps> = ({
  pallet,
  onToggleExpanded,
  onDestinationOverride,
  onQrPrint,
}) => {
  const { hasPermissions } = usePermissionManager()

  const palletRow = pallet.goodsReceiptPalletRow
  const palletLots = palletRow?.lots ?? []
  const product = palletRow?.product
  const goodsReceipt = palletRow?.goodsReceipt

  const isAkrochemManaged = isAkrochemManagedWarehouse(goodsReceipt?.warehouseArea?.type)
  const shouldShowOverrideDestination =
    !!onDestinationOverride &&
    hasPermissions([PermissionActionType.LOCATION_OVERRIDE]) &&
    isAkrochemManaged &&
    goodsReceipt?.status !== GoodsReceiptStatus.Stored

  const units = useUnitOfMeasurementsStore(useShallow((s) => s.units))
  const uomId = palletRow?.product?.packageUsWeightUom
  const uomName = getUomName(units, uomId)

  const { palletPackages, currentPalletUom } = palletLots.reduce(
    (acc, lot) => ({
      palletPackages: acc.palletPackages + (lot.units ?? 0),
      currentPalletUom: acc.currentPalletUom + (lot.totalUom ?? 0),
    }),
    { palletPackages: 0, currentPalletUom: 0 }
  )

  const detailFields: DetailField[] = [
    ["PO #:", palletRow?.order?.orderId],
    ["GR #:", palletRow?.goodsReceipt?.agrNumber],
    ["AS400 #:", palletRow?.order?.as400OrderNumber],
    ["Vendor Description:", product?.companyProductDescription],
    ["Supplier:", product?.company?.name],
    [
      "Current Location:",
      pallet.destinationWarehouseShelving && palletRow?.goodsReceipt?.warehouseArea
        ? `${pallet.destinationWarehouseShelving.aisleCode}, ${pallet.destinationWarehouseShelving.sectionCode}, ${pallet.destinationWarehouseShelving.binNumber} - ${formatWarehouseLabel(palletRow?.goodsReceipt?.warehouseArea)}`
        : "-",
    ],
    ["Full Pallet UoM:", `${product?.palletUsWeight ?? "-"} ${uomName}`],
    ["Full Pallet Packages:", product?.packagesPerPallet ?? "-"],
    ["Current Pallet UoM:", `${currentPalletUom} ${uomName}`],
    ["Allocated Pallet UoM with SO #:", null],
    ["Current Pallet Packages:", palletPackages],
    ["Country of Origin:", product?.countryOrigin?.country?.name],
  ]

  return (
    <Box className='mt-4'>
      <DetailFieldList fields={detailFields} />

      {palletLots?.length > 0 && (
        <Box className='mb-6 grid grid-cols-2 gap-4'>
          {palletLots.map((lot) => (
            <DetailFieldList key={lot.goodsReceiptPalletRowLotId} fields={createLotDetails(lot, uomName)} />
          ))}
        </Box>
      )}

      <Box className='flex items-center justify-between gap-4'>
        <Link
          href='#'
          onClick={(e) => {
            e.preventDefault()
            onToggleExpanded()
          }}
          className='font-medium no-underline'
          data-testid='less-details-link'
        >
          Less Details
        </Link>
        {shouldShowOverrideDestination && (
          <Link
            href='#'
            onClick={(e) => {
              e.preventDefault()
              onDestinationOverride()
            }}
            className='font-medium no-underline'
            data-testid='override-storage-link'
          >
            Override Storage Location
          </Link>
        )}
        <Link
          href='#'
          onClick={(e) => {
            e.preventDefault()
            onQrPrint()
          }}
          className='font-medium no-underline'
          data-testid='print-qr-link'
        >
          Print QR Code
        </Link>
      </Box>
    </Box>
  )
}
